import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import {
  HomeIcon,
  LinkIcon,
  CreditCardIcon,
  Cog6ToothIcon,
  ArrowLeftOnRectangleIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  ShieldExclamationIcon,
  ShareIcon,
  BanknotesIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
  ClipboardDocumentListIcon,
  FolderIcon,
  BellIcon,
  KeyIcon,
  PaintBrushIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';
import Logo from '../common/Logo';
import { useAuth } from '../../context/AuthContext';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'MFA Status', href: '/dashboard/mfa', icon: ShieldCheckIcon },
  { name: 'Devices', href: '/dashboard/devices', icon: DevicePhoneMobileIcon },
  { name: 'Security Alerts', href: '/dashboard/security', icon: ShieldExclamationIcon },
  { name: 'File Sharing', href: '/dashboard/file-sharing', icon: ShareIcon },
  { name: 'Remediation', href: '/dashboard/remediation', icon: WrenchScrewdriverIcon },
  { name: 'Tasks', href: '/dashboard/tasks', icon: ClipboardDocumentListIcon },
  { name: 'Evidence Locker', href: '/dashboard/evidence', icon: FolderIcon },
  { name: 'Notifications', href: '/dashboard/notifications', icon: BellIcon },
  { name: 'Connect', href: '/dashboard/connect', icon: LinkIcon },
  { name: 'Team', href: '/dashboard/team', icon: UserGroupIcon },
  { name: 'Plans', href: '/dashboard/plans', icon: CreditCardIcon },
  { name: 'Billing', href: '/dashboard/billing', icon: BanknotesIcon },
  { name: 'API Keys', href: '/dashboard/api-keys', icon: KeyIcon, enterprise: true },
  { name: 'Branding', href: '/dashboard/branding', icon: PaintBrushIcon, enterprise: true },
  { name: 'MSP Portal', href: '/dashboard/msp', icon: BuildingOffice2Icon, enterprise: true },
  { name: 'Settings', href: '/dashboard/settings', icon: Cog6ToothIcon },
];

export default function Sidebar({ mobile = false, onClose }) {
  const location = useLocation();
  const { logout, user, tenant } = useAuth();

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    if (onClose) onClose();
  };

  return (
    <div className={clsx(
      'flex flex-col h-full bg-white dark:bg-slate-900',
      'border-r border-slate-200 dark:border-slate-800',
      mobile ? 'w-full' : 'w-64'
    )}>
      {/* Logo */}
      <div className="flex items-center h-16 px-6 border-b border-slate-200 dark:border-slate-800">
        <Logo linkTo="/dashboard" />
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
        {navigation
          .filter((item) => {
            // Hide enterprise features unless on enterprise plan
            if (item.enterprise) {
              const planSlug = tenant?.subscription?.plan?.slug || tenant?.plan?.slug;
              return planSlug === 'enterprise';
            }
            return true;
          })
          .map((item) => (
          <Link
            key={item.name}
            to={item.href}
            onClick={onClose}
            className={clsx(
              'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
              isActive(item.href)
                ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white'
            )}
          >
            <item.icon className="h-5 w-5" />
            {item.name}
          </Link>
        ))}
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
            <span className="text-sm font-medium text-primary-700 dark:text-primary-400">
              {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
              {user?.name || 'User'}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
              {tenant?.name || 'No workspace'}
            </p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-3 py-2.5 mt-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white transition-colors"
        >
          <ArrowLeftOnRectangleIcon className="h-5 w-5" />
          Log out
        </button>
      </div>
    </div>
  );
}
